import { Injectable } from '@angular/core';
import { coreVendors } from '@nexxe/core-vendors';
import { Observable } from 'rxjs';
import { CdsMetadata } from '../class/cds-metadata';
import { Status } from '../enums/status';
import { ParamsType } from '../interfaces/icds';
import { ICDSConfig } from '../interfaces/icdsconfig';
import { CdsDataComposerService } from './cds-data-composer.service';
import { CdsRequestService } from './cds-request.service';
import { CdsUtilitiesService } from './cds-utilities.service';


export function MethodDepricator(deprecation: IMethodDeprecatorMetaData): MethodDecorator {
	const innerFunc = function (target: Function, name: string, descriptor: any) {

		const originalMethod = descriptor.value;
		const message = deprecation.message ?
			deprecation.message :
			`The method "${<string>name}", in the class "${target.constructor.name}", is deprecated and will no longer be supported`
			+ (deprecation.date ? ` from ${deprecation.date}.` : ` in the future releases.`)
			+ (deprecation.alternate ? ` Please use ${deprecation.alternate} instead.` : ``)


		descriptor.value = function (...args: any[]) {
			console.warn(message);
			return originalMethod.apply(this, args);
		}

		return descriptor;
	}
	return innerFunc;
}

export interface IMethodDeprecatorMetaData {
	message?: string;
	date?: string;
	alternate?: string;
}

@Injectable({
	providedIn: 'root'
})
export class CdsService {

	constructor(
		private utilities: CdsUtilitiesService,
		private cdsRequest: CdsRequestService,
		private cdsComposer: CdsDataComposerService
	) {
		CdsMetadata.cdsDataComposer = this.cdsComposer;
	}
	@MethodDepricator({
		date: '04/01/2021',
		alternate: 'the method "load" in the "ConfigurableDataSourceService" class'
	})
	load(config: ICDSConfig, storeId: string = 'default') {
		//this is to fix the signature of the object comes up from from the CDN.
		if (Array.isArray(config.source)) {
			config.source = config.source[0];
		}
		this.utilities.registerMetadata(config, storeId);
	}

	private loadExisting(storeId: string) {
		this.utilities.registerExistingMetaData(storeId);
	}

	unload(storeId: string) {
		this.utilities.removeConfigFromStore(storeId);
	}

	existsInStore(storeId: string) {
		return this.utilities.alreadyExistsInStore(storeId);
	}

	/**
	 * 
	 * @param key CDS ID
	 * @param params params required 
	 * @param payload in case of the call type is post
	 * @param override to inforce invoking a new API everytime
	 */
	@MethodDepricator({
		date: '04/01/2021',
		alternate: 'the method "request" in the "ConfigurableDataSourceService" class'
	})
	request(keys: string | Array<string>, params?: ParamsType | Array<ParamsType>, payload?: ParamsType | Array<ParamsType>, urltemplate?: Object | Array<Object>, override?: boolean | Array<boolean>, storeId: string = 'default'): Observable<any> {
		this.loadExisting(storeId);
		return Array.isArray(keys) ? this.requestMultiple(keys, <Array<ParamsType>>params, <Array<ParamsType>>payload, <Array<object>>urltemplate, <Array<boolean>>override) : this.requestSingle(keys, params, payload, urltemplate, <boolean>override);
	}

	@MethodDepricator({
		date: '04/01/2021',
		alternate: 'the method "requestWithFormData" in the "ConfigurableDataSourceService" class'
	})
	requestWithFormData(key: string, params?: ParamsType, payload?: ParamsType, urltemplate?: Object) {
		const id = this.utilities.createNewRequest(key, params, payload, urltemplate, true);
		this.executeRequest(key, id);
		return this.utilities.getFormDataResponse(key, id);
	}

	private requestSingle(key: string, params?: ParamsType, payload?: ParamsType, urltemplate?: Object, override?: boolean) {
		const id = this.utilities.createNewRequest(key, params, payload, urltemplate, false, override);
		this.executeRequest(key, id);
		return this.utilities.getResponse(key, id);
	}

	private requestMultiple(keys: Array<string>, params?: Array<ParamsType>, payload?: Array<ParamsType>, urltemplate?: Array<Object>, override?: Array<boolean>): Observable<any> {
		let ids = [];
		coreVendors.lodash.forEach(keys, (key, index) => {
			const id = this.utilities.createNewRequest(key, params && params[index], payload && payload[index], urltemplate && urltemplate[index], false, override && override[index]);
			ids.push(id);
		})
		this.executeRequest(keys, ids);
		return this.utilities.getResponse(keys, ids);
	}

	///TO DO execute
	private executeRequest(keys: string | Array<string>, ids: string | Array<string>) {
		if (typeof ids === 'string' && typeof keys === 'string') {
			this.executeSingleRequest(keys, ids)
		} else if (Array.isArray(keys) && Array.isArray(ids)) {
			this.executeMultipleRequests(keys, ids);
		}
	}

	private executeSingleRequest(key: string, id: string) {
		const status = this.utilities.getMetadataStatus(key, id);
		if (status == Status.success) {
			this.utilities.publishResponse(key, id);
		}
		else if (status == Status.notUsed) {
			this.cdsRequest.execute(<CdsMetadata>this.utilities.getCdsMetadata(key, id));
		} else if (status === Status.progress) {
			this.utilities.getInProgressResponse(key, id).subscribe(() => {
				this.utilities.publishResponse(key, id);
			});
		}

	}

	private executeMultipleRequests(keys: Array<string>, ids: Array<string>) {
		const responses = {};
		const cdsMetaDataArray = Array<CdsMetadata>();
		const pendingResponses = Array<{ obs: Observable<any>, key: string, id: string }>();
		coreVendors.lodash.forEach(keys, (key, index) => {
			const status = this.utilities.getMetadataStatus(key, ids[index]);
			if (status == Status.success) {
				responses[key] = this.utilities.getExistingResponse(key, ids[index]);
			}
			else if (status == Status.notUsed) {
				cdsMetaDataArray.push(<CdsMetadata>this.utilities.getCdsMetadata(key, ids[index]));
			}
			else if (status == Status.progress) {
				pendingResponses.push({ obs: this.utilities.getInProgressResponse(key, index), key, id: ids[index] });
			}
		})
		this.cdsRequest.executeMultiple(cdsMetaDataArray, keys, ids, responses, pendingResponses);
	}

	registerServices(services: { [key: string]: Object }) {
		this.cdsComposer.dataModelService = services['DmDataModelManagerService'];
	}
}


